import React, { useEffect, useMemo } from 'react';

import { useInView } from 'react-intersection-observer';

import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { useAppSelector } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { useManagerFilterProps } from '@app/pages/Prototype/NftManagerPanel/hooks';
import { type NFTItem } from '@app/store/types/nft-manager';

import { NftPlaceholderCard } from '../../../NftPlaceholderCard';

import { NftCard, type NftCardProps, NftCardsEmpty, NoMatchingResults, NoMatchingUnsupported } from './components';

import { Content, ManagerGridWrapper, Pagination } from './styles';

// TODO update to just use media queries
const NFT_COLUMNS = 4;
const NFT_COLLECTION_COLUMNS = 3;
const MOBILE_COLUMNS = 2;

export const DEFAULT_QUERY_LIMIT = 20;
const SKELETON_LOADING_ITEMS: number[] = [...Array(DEFAULT_QUERY_LIMIT)].map((_value, index) => index + 1);

export type IsDisabledPageItem = (item: NFTItem) => boolean;

type ManagerGridProps = Pick<
    NftCardProps,
    'handleAddItem' | 'handleIsSelected' | 'handleRemoveItem' | 'handleSelect' | 'handleIsDisabledPageItem'
> & {
    collections?: boolean;
    isLoading: boolean;
    hasNextPage: boolean;
    nftCards?: NFTItem[];
    fetchNextPage(): void;
    onClose: () => void;
    handleIsDisabledPageItem: IsDisabledPageItem;
};

export function ManagerGrid({
    collections,
    hasNextPage,
    isLoading,
    nftCards,
    onClose,
    fetchNextPage,
    handleAddItem,
    handleRemoveItem,
    handleIsSelected,
    handleIsDisabledPageItem,
    handleSelect,
}: ManagerGridProps) {
    const { tablet } = useMediaQuery();
    const { ref, inView } = useInView();

    const unsupportedNfts = useAppSelector((state) => state.nftManager.unsupportedNfts);
    const managerFilters = useAppSelector((state) => state.nftManager.managerFilters);
    const filtersCount = Object.values(managerFilters).filter(Boolean).length;

    const columns = collections ? NFT_COLLECTION_COLUMNS : NFT_COLUMNS;

    const { searchValue } = useManagerFilterProps();

    useEffect(() => {
        if (inView && hasNextPage && !isLoading) {
            fetchNextPage();
        }
    }, [fetchNextPage, hasNextPage, inView, isLoading]);

    const NftPlaceHolders = useMemo(() => {
        if (isLoading) {
            return SKELETON_LOADING_ITEMS.map((_, index) => <NftPlaceholderCard key={index} custom={index} />);
        }
    }, [isLoading]);

    const NftCardsMarkup = useMemo(
        () =>
            nftCards?.map((item) => (
                <NftCard
                    key={item.id}
                    item={item}
                    handleIsDisabledPageItem={handleIsDisabledPageItem}
                    handleAddItem={handleAddItem}
                    handleRemoveItem={handleRemoveItem}
                    handleIsSelected={handleIsSelected}
                    handleSelect={handleSelect}
                />
            )),
        [handleAddItem, handleIsSelected, handleRemoveItem, handleSelect, nftCards, handleIsDisabledPageItem],
    );

    if (!nftCards?.length && !isLoading) {
        if (filtersCount || searchValue.length) {
            if (unsupportedNfts && unsupportedNfts.length > 0) {
                return <NoMatchingUnsupported />;
            }
            return <NoMatchingResults />;
        }
        return <NftCardsEmpty onClose={onClose} />;
    }
    // TODO: This "load more" string needs to come from i18n.
    const PaginationMarkup = hasNextPage ? (
        <Pagination>
            <Button ref={ref} disabled={isLoading} loading={isLoading} onClick={() => fetchNextPage()}>
                Load more
            </Button>
        </Pagination>
    ) : null;

    return (
        <ManagerGridWrapper>
            <Content $columns={tablet ? columns : MOBILE_COLUMNS}>
                {NftCardsMarkup}
                {NftPlaceHolders}
            </Content>

            {PaginationMarkup}
        </ManagerGridWrapper>
    );
}
